import { useApolloClient, useMutation } from '@apollo/client';

import type { JobUserInteractions } from '@xing-com/crate-common-graphql-types';
import { JobBookmarkState } from '@xing-com/crate-common-graphql-types';
import type {
  DeleteJobBookmarkMutation,
  JobTeaserVisibleJobUserInteractionsFragment,
  JobUpsertBookmarkMutation,
} from '@xing-com/crate-jobs-graphql';
import {
  DeleteJobBookmarkDocument,
  JobTeaserVisibleJobUserInteractionsFragmentDoc,
  JobUpsertBookmarkDocument,
} from '@xing-com/crate-jobs-graphql';
import { useBookmarkTracking } from '@xing-com/crate-jobs-tracking';
import type {
  NwtTrackingProps,
  OmitFromUnion,
} from '@xing-com/crate-jobs-tracking';

type UseBookmarkOptions = {
  jobId: string;
  isBookmarked: boolean;
  nwtTrackingOptions: OmitFromUnion<NwtTrackingProps, 'event'>;
  onCompleted?: () => void;
  onError?: () => void;
};

export const useJobBookmarkToggle = ({
  jobId,
  isBookmarked,
  nwtTrackingOptions,
  onCompleted,
  onError,
}: UseBookmarkOptions): (() => void) => {
  const { cache } = useApolloClient();
  const trackNwtJobsBookmark = useBookmarkTracking({
    itemUrn: `surn:x-xing:jobs:posting:${jobId}`,
    ...nwtTrackingOptions,
  });

  const getCachedUserInteractions = ():
    | JobUserInteractions
    | null
    | undefined => {
    const cacheId = cache.identify({
      __typename: 'VisibleJob',
      id: jobId,
    });

    if (!cacheId) {
      return null;
    }

    return (
      cache.readFragment<JobTeaserVisibleJobUserInteractionsFragment>({
        id: cacheId,
        fragment: JobTeaserVisibleJobUserInteractionsFragmentDoc,
        returnPartialData: true,
      })?.userInteractions ?? null
    );
  };

  const optimisticResponse:
    | JobUpsertBookmarkMutation
    | DeleteJobBookmarkMutation = {
    __typename: 'Mutation',
    [isBookmarked ? 'jobDeleteBookmark' : 'jobUpsertBookmark']: {
      __typename: isBookmarked ? 'JobBookmarkMutationResult' : 'JobBookmark',
      job: {
        __typename: 'VisibleJob',
        id: jobId,
        userInteractions: {
          __typename: 'JobUserInteractions',
          application: getCachedUserInteractions()?.application,
          bookmark: {
            __typename: 'JobBookmark',
            state: isBookmarked
              ? JobBookmarkState.NotBookmarked
              : JobBookmarkState.Saved,
          },
        },
      },
    },
  };

  const [upsertBookmarkMutation] = useMutation(JobUpsertBookmarkDocument, {
    variables: { id: jobId, state: JobBookmarkState.Saved },
    optimisticResponse,
    onCompleted,
    onError,
  });

  const [deleteBookmarkMutation] = useMutation(DeleteJobBookmarkDocument, {
    variables: { id: jobId },
    optimisticResponse,
    onCompleted,
    onError,
  });

  const bookmarkMutation = isBookmarked
    ? deleteBookmarkMutation
    : upsertBookmarkMutation;

  return () => {
    bookmarkMutation();
    trackNwtJobsBookmark(isBookmarked);
  };
};
